import { api } from './api';
import {
  UserDetails,
  ShipmentStatusCount,
  ShipmentCountResponse,
  CourierDistribution,
  ZoneDistribution,
  RegionDistribution,
  CityDistribution,
  RTOCityDistribution,
  PaymentModeDistribution,
  ServiceTypeDistribution,
} from '../types/dashboard';
import { User } from '../types/auth';

export const dashboardService = {
  async getUserDetails(userId: string): Promise<User> {
    const response = await api.get<UserDetails>(`/user-details/${userId}`);
    const u = response.data;
    const raw = u as any;

    return {
      ...raw,
      id: String(u.userId ?? raw.id ?? ''),
      email: u.email ?? raw.email,
      firstName: u.firstName ?? raw.first_name ?? raw.firstName,
      lastName: u.lastName ?? raw.last_name ?? raw.lastName,
      companyName: u.companyName ?? raw.company_name ?? raw.companyName,
      mobileNo: u.mobileNo ?? raw.mobile_no ?? raw.mobileNo,
      active: u.active ?? raw.active,
      roleId: u.roleId ?? raw.roleId ?? raw.role_id,
      roleName: u.roleName ?? raw.roleName ?? raw.role_name,
      crn_number: raw.crn_number ?? raw.crnNumber ?? '',
    };
  },

  async getShipmentStatusCount(
    userId: string,
    fromDate: string,
    toDate: string
  ): Promise<ShipmentStatusCount> {
    const response = await api.get<ShipmentStatusCount>(
      `/dashboard/shipment-distribution/${userId}?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response.data;
  },

  async getShipmentCount(
    userId: string,
    fromDate: string,
    toDate: string
  ): Promise<ShipmentCountResponse> {
    const response = await api.get<ShipmentCountResponse>(
      `/dashboard/shipment-count/${userId}?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response.data;
  },

  async getCourierDistribution(
    userId: string,
    fromDate: string,
    toDate: string
  ): Promise<CourierDistribution[]> {
    const response = await api.get<CourierDistribution[]>(
      `/dashboard/courier-distribution/${userId}?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response.data;
  },

  async getZoneDistribution(
    userId: string,
    fromDate: string,
    toDate: string
  ): Promise<ZoneDistribution[]> {
    const response = await api.get<ZoneDistribution[]>(
      `/dashboard/zone-distribution/${userId}?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response.data;
  },

  async getRegionDistribution(
    userId: string,
    fromDate: string,
    toDate: string
  ): Promise<RegionDistribution[]> {
    const response = await api.get<RegionDistribution[]>(
      `/dashboard/region-distribution/${userId}?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response.data;
  },
  async getShipmentCharges(
    userId: string,
    fromDate: string,
    toDate: string
  ): Promise<any[]> {
    const response = await api.get<any[]>(
      `/dashboard/shipment-charges/${userId}?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response.data;
  },

  async getCityDistributionCount(
    userId: string,
    fromDate: string,
    toDate: string
  ): Promise<CityDistribution[]> {
    const response = await api.get<CityDistribution[]>(
      `/dashboard/top-cities/${userId}?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response.data;
  },

  async getRTOCityDistributionCount(
    userId: string,
    fromDate: string,
    toDate: string
  ): Promise<RTOCityDistribution[]> {
    const response = await api.get<RTOCityDistribution[]>(
      `/dashboard/top-rto-cities/${userId}?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response.data;
  },

  async getModeAndTypeDistributions(
    userId: string,
    fromDate: string,
    toDate: string
  ): Promise<{
    paymentModeDistribution: PaymentModeDistribution[];
    serviceTypeDistribution: ServiceTypeDistribution[];
  }> {
    const response = await api.get<{
      paymentModeDistribution: PaymentModeDistribution[];
      serviceTypeDistribution: ServiceTypeDistribution[];
    }>(
      `/dashboard/mode-and-type-distributions/${userId}?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response.data;
  },

  async getRTOAnalysis(
    userId: string,
    fromDate: string,
    toDate: string
  ): Promise<any> {
    const response = await api.get<any>(
      `/dashboard/rto-analysis/${userId}?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response.data;
  },
};
